import React, { useState, useEffect } from 'react';
import './App.css';

function App() {
  const [iconsData, setIconsData] = useState(null);
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [activeTab, setActiveTab] = useState('html');
  const [weight, setWeight] = useState('regular');
  const [color, setColor] = useState('rgba(235, 87, 87, 0.99)');
  const [size, setSize] = useState('');

  useEffect(() => {
    fetch(process.env.PUBLIC_URL + '/icons-data.json')
      .then(res => res.json())
      .then(data => setIconsData(data))
      .catch(() => {
        // Fallback data with actual icon names
        setIconsData({
          icons: ['behance', 'discord', 'dribble', 'email', 'flickr', 'github', 'link', 'linkedin', 'location', 'paypal', 'phone', 'pinterest', 'reddit', 'signal', 'snapchat', 'soundcloud', 'spotify', 'telegram', 'thread', 'tiktok', 'twitch', 'viemo', 'website', 'wechat', 'whatsapp', 'x'],
          weights: ['thin', 'light', 'regular', 'solid'],
          implementation: {
            react: {
              install: 'npm install @tapmo-india/icons',
              component: 'import { TapMoIcon } from "@tapmo-india/icons/react";'
            },
            reactNative: {
              install: 'npm install @tapmo-india/icons',
              component: 'import { TapMoIcon } from "@tapmo-india/icons/react-native";'
            }
          }
        });
      });
  }, []);

  const openPopup = (iconName) => {
    setSelectedIcon(iconName);
    setWeight('regular');
    setColor('rgba(235, 87, 87, 0.99)');
    setSize('');
    setActiveTab('html');
  };

  const closePopup = () => {
    setSelectedIcon(null);
  };

  if (!iconsData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-800 text-xl">Loading TapMo Icons...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="https://card.tapmo.in/assets/uploads/logos/logo_tapmo.png" 
                alt="TapMo Logo" 
                className="h-10 w-auto"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-800">TapMo Icons</h1>
                <p className="text-gray-600 text-sm">Professional Icon Library</p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-gray-600">
              <span className="text-sm">{iconsData.icons.length} Icons</span>
              <a href="https://tapmo.in" className="text-sm hover:text-gray-800 transition-colors">tapmo.in</a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">
            Social Media Icons Collection
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Complete set of social media icons with multiple weights and customizable styling. 
            Click any icon to get implementation code and explore customization options.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-4">
          {iconsData.icons.map(icon => (
            <div 
              key={icon}
              className="bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all cursor-pointer text-center group"
              onClick={() => openPopup(icon)}
            >
              <i className={`tm-${icon} text-4xl mb-3 block text-red-500 group-hover:text-black transition-colors`}></i>
              <div className="text-xs text-gray-600 font-medium">tm-{icon}</div>
            </div>
          ))}
        </div>
      </div>

      {selectedIcon && (
        <IconPopup 
          icon={selectedIcon}
          iconsData={iconsData}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          weight={weight}
          setWeight={setWeight}
          color={color}
          setColor={setColor}
          size={size}
          setSize={setSize}
          onClose={closePopup}
        />
      )}
    </div>
  );
}

const IconPopup = ({ icon, iconsData, activeTab, setActiveTab, weight, setWeight, color, setColor, size, setSize, onClose }) => {
  const [copied, setCopied] = useState('');

  const getIconClass = () => {
    let classes = [`tm-${icon}`];
    if (weight !== 'regular') classes.push(`tm-${weight}`);
    return classes.join(' ');
  };

  const getPreviewSize = () => {
    const sizeMap = {
      'xs': 'text-4xl',
      'sm': 'text-5xl', 
      '': 'text-8xl',
      'lg': 'text-9xl',
      'xl': 'text-[10rem]',
      '2xl': 'text-[12rem]'
    };
    return sizeMap[size] || 'text-8xl';
  };

  const getHtmlCode = () => {
    let classes = getIconClass();
    if (size) classes += ` tm-${size}`;
    return `<i class="${classes}"></i>`;
  };

  const getReactCode = () => {
    return `<TapMoIcon name="${icon}"${weight !== 'regular' ? ` weight="${weight}"` : ''}${color !== '#000000' ? ` color="${color}"` : ''} />`;
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(type);
      setTimeout(() => setCopied(''), 2000);
    });
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose}></div>
      <div className="fixed inset-4 bg-white rounded-2xl shadow-2xl z-50 overflow-hidden flex flex-col max-w-6xl mx-auto border border-gray-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-4">
            <button 
              className="text-2xl font-bold text-gray-800 hover:text-gray-600 cursor-pointer flex items-center gap-2"
              onClick={() => copyToClipboard(icon, 'name')}
              title="Copy icon name"
            >
              {icon}
              {copied === 'name' ? 
                <span className="text-sm text-green-600">✓</span> : 
                <span className="text-sm text-gray-400">📋</span>
              }
            </button>
            <button className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium">
              TapMo
            </button>
          </div>
          <button 
            className="w-8 h-8 rounded-full bg-white/80 hover:bg-white flex items-center justify-center text-gray-600 hover:text-gray-800 transition-colors"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left Panel - Preview */}
          <div className="w-1/2 p-6 border-r flex flex-col">
            <div className="flex-1 flex items-center justify-center bg-gray-50 rounded-xl mb-6 border border-gray-200">
              <i 
                className={`${getIconClass()} ${getPreviewSize()}`}
                style={{ color }}
              ></i>
            </div>

            {/* Controls */}
            <div className="space-y-6">
              {/* Weight */}
              <div>
                <label className="block text-sm font-medium mb-2">Weight:</label>
                <div className="grid grid-cols-4 gap-2">
                  {iconsData.weights.map(w => (
                    <button
                      key={w}
                      className={`p-3 border rounded text-center ${weight === w 
                        ? 'bg-gray-200 border-gray-400' 
                        : 'hover:bg-gray-50'}`}
                      onClick={() => setWeight(w)}
                    >
                      <i className={`tm-${icon}${w !== 'regular' ? ` tm-${w}` : ''} text-xl block mb-1`}></i>
                      <span className="text-xs">{w}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Color */}
              <div>
                <label className="block text-sm font-medium mb-2">Color:</label>
                <div className="flex gap-2">
                  <button 
                    className={`w-8 h-8 rounded border-2 ${color === 'rgba(235, 87, 87, 0.99)' ? 'border-gray-400' : 'border-gray-200'}`}
                    style={{ backgroundColor: 'rgba(235, 87, 87, 0.99)' }}
                    onClick={() => setColor('rgba(235, 87, 87, 0.99)')}
                  ></button>
                  <button 
                    className={`w-8 h-8 rounded border-2 ${color === '#000000' ? 'border-gray-400' : 'border-gray-200'}`}
                    style={{ backgroundColor: '#000000' }}
                    onClick={() => setColor('#000000')}
                  ></button>
                  <button 
                    className={`w-8 h-8 rounded border-2 ${color === '#ffffff' ? 'border-gray-400' : 'border-gray-200'}`}
                    style={{ backgroundColor: '#ffffff', border: '1px solid #ccc' }}
                    onClick={() => setColor('#ffffff')}
                  ></button>
                  <input 
                    type="color" 
                    value={color} 
                    onChange={(e) => setColor(e.target.value)}
                    className="w-8 h-8 rounded border-2 border-gray-200"
                  />
                </div>
              </div>

              {/* Size */}
              <div>
                <label className="block text-sm font-medium mb-2">Size:</label>
                <div className="flex gap-2 flex-wrap">
                  {['xs', 'sm', '', 'lg', 'xl', '2xl'].map(s => (
                    <button
                      key={s || 'default'}
                      className={`px-3 py-1 border rounded text-sm ${size === s 
                        ? 'bg-gray-200 border-gray-400' 
                        : 'hover:bg-gray-50'}`}
                      onClick={() => setSize(s)}
                    >
                      {s || 'default'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Code */}
          <div className="w-1/2 flex flex-col">
            {/* Tabs */}
            <div className="flex border-b">
              {['html', 'react', 'react-native', 'vue'].map(tab => (
                <button
                  key={tab}
                  className={`px-4 py-3 text-sm font-medium uppercase tracking-wide ${activeTab === tab 
                    ? 'border-b-2 border-blue-500 text-blue-600' 
                    : 'text-gray-500 hover:text-gray-700'}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab === 'react-native' ? 'React Native' : tab}
                </button>
              ))}
            </div>

            {/* Code Content */}
            <div className="flex-1 p-6">
              {activeTab === 'html' && (
                <div>
                  <div className="relative">
                    <div className="bg-gray-900 text-green-400 p-4 rounded font-mono text-sm mb-4">
                      <code>{getHtmlCode()}</code>
                    </div>
                    <button 
                      className="absolute top-2 right-2 px-2 py-1 bg-gray-700 text-white text-xs rounded hover:bg-gray-600"
                      onClick={() => copyToClipboard(getHtmlCode(), 'html')}
                    >
                      {copied === 'html' ? '✓ Copied' : 'Copy'}
                    </button>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p className="mb-2">Copy the HTML code above to use this icon in your project.</p>
                    <p>Make sure to include the TapMo Icons CSS file in your HTML head.</p>
                  </div>
                </div>
              )}

              {activeTab === 'react' && (
                <div>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium mb-2">1. Install & Import:</h4>
                      <div className="relative">
                        <div className="bg-gray-900 text-green-400 p-3 rounded font-mono text-sm">
                          <code>{iconsData.implementation?.react?.install || 'npm install @tapmo-india/icons'}</code>
                        </div>
                        <button 
                          className="absolute top-2 right-2 px-2 py-1 bg-gray-700 text-white text-xs rounded hover:bg-gray-600"
                          onClick={() => copyToClipboard(iconsData.implementation?.react?.install || 'npm install @tapmo-india/icons', 'install')}
                        >
                          {copied === 'install' ? '✓' : 'Copy'}
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-medium mb-2">2. Usage:</h4>
                      <div className="relative">
                        <div className="bg-gray-900 text-green-400 p-3 rounded font-mono text-sm">
                          <code>{getReactCode()}</code>
                        </div>
                        <button 
                          className="absolute top-2 right-2 px-2 py-1 bg-gray-700 text-white text-xs rounded hover:bg-gray-600"
                          onClick={() => copyToClipboard(getReactCode(), 'react')}
                        >
                          {copied === 'react' ? '✓' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'react-native' && (
                <div>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium mb-2">1. Install:</h4>
                      <div className="relative">
                        <div className="bg-gray-900 text-green-400 p-3 rounded font-mono text-sm">
                          <code>npm install @tapmo-india/icons</code>
                        </div>
                        <button 
                          className="absolute top-2 right-2 px-2 py-1 bg-gray-700 text-white text-xs rounded hover:bg-gray-600"
                          onClick={() => copyToClipboard('npm install @tapmo-india/icons', 'rn-install')}
                        >
                          {copied === 'rn-install' ? '✓' : 'Copy'}
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-medium mb-2">2. Usage:</h4>
                      <div className="relative">
                        <div className="bg-gray-900 text-green-400 p-3 rounded font-mono text-sm">
                          <code>{`<TapMoIcon name="${icon}" size={24} color="${color}" />`}</code>
                        </div>
                        <button 
                          className="absolute top-2 right-2 px-2 py-1 bg-gray-700 text-white text-xs rounded hover:bg-gray-600"
                          onClick={() => copyToClipboard(`<TapMoIcon name="${icon}" size={24} color="${color}" />`, 'rn-usage')}
                        >
                          {copied === 'rn-usage' ? '✓' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'vue' && (
                <div>
                  <div className="relative">
                    <div className="bg-gray-900 text-green-400 p-4 rounded font-mono text-sm mb-4">
                      <code>{`<i class="${getIconClass()}"></i>`}</code>
                    </div>
                    <button 
                      className="absolute top-2 right-2 px-2 py-1 bg-gray-700 text-white text-xs rounded hover:bg-gray-600"
                      onClick={() => copyToClipboard(`<i class="${getIconClass()}"></i>`, 'vue')}
                    >
                      {copied === 'vue' ? '✓ Copied' : 'Copy'}
                    </button>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>Vue implementation coming soon...</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default App;